import _ from 'lodash';

const DEFAULT_EXCLUDED_ROOT_KEYS = new Set([
    '_id',
    'version',
    'createdAt',
    'updatedAt',
    'updatedBy',
    '_localId',
    '_isNew',
]);

// These keys exist in React form state for UX only and must never reach update-field APIs.
const TRANSIENT_FRONTEND_ROOT_KEYS = new Set([
    'isRegisteredAddressSame',
    'isMailingAddressSame',
]);

const TRANSIENT_PAYLOAD_LEAF_KEYS = new Set([
    '_localId',
    '_isNew',
]);

const INVALID_PATH_SEGMENTS = new Set([
    'undefined',
    'null',
]);

// Address autocomplete and UI helpers can attach these leaves even though backend models
// do not persist them.
const TRANSIENT_ADDRESS_HELPER_LEAF_KEYS = new Set([
    'hidePinCode',
    'ref_id',
    'name',
    'lat',
    'lng',
    'notAccurate',
]);

const ADDRESS_LIKE_SEGMENTS = new Set([
    'address',
    'addressList',
    'mailingAddress',
    'communicationAddress',
]);

export const cloneAutoSaveValue = (value) => _.cloneDeep(value);

const AUTHORITATIVE_PAYLOAD_KEYS = new Set([
    '_id',
    'version',
    'createdAt',
    'updatedAt',
    'updatedBy',
    'status',
    'seq',
    'aEId',
    'sdId',
    'aiId',
    'amlId',
    'kycId',
    'cyclosUserId',
    'cyclosUserName',
    'application_id',
    'ubo_id',
]);

const mergeUpdatePayloadWithAuthoritativeData = (authoritativeValue, candidateValue, key = null) => {
    if (candidateValue === undefined) {
        return _.cloneDeep(authoritativeValue);
    }

    if (
        key
        && AUTHORITATIVE_PAYLOAD_KEYS.has(key)
        && authoritativeValue !== undefined
        && authoritativeValue !== null
    ) {
        return _.cloneDeep(authoritativeValue);
    }

    if (Array.isArray(candidateValue)) {
        const authoritativeArray = Array.isArray(authoritativeValue) ? authoritativeValue : [];
        return candidateValue.map((candidateItem, index) => {
            const authoritativeItem = authoritativeArray[index];
            if (_.isPlainObject(authoritativeItem) && _.isPlainObject(candidateItem)) {
                return mergeUpdatePayloadWithAuthoritativeData(authoritativeItem, candidateItem);
            }
            return _.cloneDeep(candidateItem);
        });
    }

    if (_.isPlainObject(candidateValue)) {
        const authoritativeObject = _.isPlainObject(authoritativeValue) ? authoritativeValue : {};
        const mergedObject = _.cloneDeep(authoritativeObject);

        Object.keys(candidateValue).forEach((childKey) => {
            mergedObject[childKey] = mergeUpdatePayloadWithAuthoritativeData(
                authoritativeObject?.[childKey],
                candidateValue[childKey],
                childKey
            );
        });

        return mergedObject;
    }

    return _.cloneDeep(candidateValue);
};

export const buildPostAutoSaveUpdatePayload = (authoritativeData, pendingPayload = null) => {
    if (!_.isPlainObject(authoritativeData)) {
        return _.cloneDeep(pendingPayload);
    }

    if (!_.isPlainObject(pendingPayload)) {
        return _.cloneDeep(authoritativeData);
    }

    return mergeUpdatePayloadWithAuthoritativeData(authoritativeData, pendingPayload);
};

export const getAutoSaveProceedBlockedMessage = (reason) => {
    switch (reason) {
        case 'out_of_date':
            return 'This form has newer saved changes. Please refresh the page and try again.';
        case 'validation':
            return 'Please fix the invalid values before continuing.';
        case 'saving':
            return 'Your latest changes are still being saved. Please try again in a moment.';
        default:
            return "We couldn't save your latest changes right now. Please try again.";
    }
};

const NUMERIC_FIELD_TYPES = new Set(['number', 'integer', 'long', 'double', 'float', 'decimal']);
const BOOLEAN_FIELD_TYPES = new Set(['boolean']);
const ARRAY_FIELD_TYPES = new Set(['array', 'multipleselection', 'list', 'array<string>', 'list<string>', 'string[]']);
const STRING_FIELD_TYPES = new Set(['string', 'text', 'email', 'date', 'singleselection', 'singleselectionlabel']);
const NUMERIC_UI_TYPES = new Set(['numberfield', 'amountfield', 'percentageinput', 'yearpicker']);
const BOOLEAN_UI_TYPES = new Set(['radioboolean']);
const ARRAY_UI_TYPES = new Set(['checkboxmultiple']);

const hasSelectableOptions = (fieldDefinition) => Array.isArray(fieldDefinition?.options)
    && fieldDefinition.options.some((option) => _.isPlainObject(option) && option.value !== undefined);

const normalizeConfiguredValueType = (valueType) => {
    const normalizedValueType = String(valueType || '').trim().toLowerCase();

    if (NUMERIC_FIELD_TYPES.has(normalizedValueType)) {
        return 'number';
    }

    if (BOOLEAN_FIELD_TYPES.has(normalizedValueType)) {
        return 'boolean';
    }

    if (ARRAY_FIELD_TYPES.has(normalizedValueType)) {
        return 'array';
    }

    if (STRING_FIELD_TYPES.has(normalizedValueType)) {
        return 'string';
    }

    return null;
};

export const toLodashPath = (path = '') => path.replace(/\[(\d+)\]/g, '.$1');

const normalizeIndexedPath = (path = '') => path
    .replace(/\[(\d+)\]/g, '[]')
    .replace(/\.([0-9]+)(?=\.|$)/g, '[]');

const getNormalizedPathSegments = (path = '') => toLodashPath(path)
    .split('.')
    .filter(Boolean);

const isAddressLikePath = (path = '') => getNormalizedPathSegments(path)
    .some((segment) => ADDRESS_LIKE_SEGMENTS.has(segment));

const isTransientRootPath = (path = '') => {
    const segments = getNormalizedPathSegments(path);
    return segments.length > 0 && TRANSIENT_FRONTEND_ROOT_KEYS.has(segments[0]);
};

const hasInvalidPathSegment = (path = '') => getNormalizedPathSegments(path)
    .some((segment) => INVALID_PATH_SEGMENTS.has(String(segment).trim().toLowerCase()));

const isTransientDiffMapPath = (path = '') => {
    const normalizedPath = toLodashPath(path);
    return normalizedPath === 'diffMap.isRegisteredAddressSame'
        || normalizedPath === 'diffMap.isMailingAddressSame'
        || normalizedPath.endsWith('.diffMap.isRegisteredAddressSame')
        || normalizedPath.endsWith('.diffMap.isMailingAddressSame');
};

const shouldStripTransientFrontendPath = (path = '', { includeDraftKeys = false } = {}) => {
    if (!path) {
        return false;
    }

    if (hasInvalidPathSegment(path)) {
        return true;
    }

    if (isTransientRootPath(path) || isTransientDiffMapPath(path)) {
        return true;
    }

    const segments = getNormalizedPathSegments(path);
    const leafKey = segments[segments.length - 1];
    if (!leafKey) {
        return false;
    }

    if (includeDraftKeys && TRANSIENT_PAYLOAD_LEAF_KEYS.has(leafKey)) {
        return true;
    }

    if (TRANSIENT_ADDRESS_HELPER_LEAF_KEYS.has(leafKey) && isAddressLikePath(path)) {
        return true;
    }

    return false;
};

const normalizeIndexedFieldPath = (path = '') => path
    .replace(/\[(\d+)\]/g, '[]')
    .replace(/\.([0-9]+)(?=\.|$)/g, '[]');

const getFieldDefinitionCandidates = (path = '') => {
    if (!path) return [];

    const normalizedIndexedPath = normalizeIndexedFieldPath(path);
    return [...new Set([path, normalizedIndexedPath].filter(Boolean))];
};

const candidateMatchesPath = (metadataPath, pathCandidates = []) => {
    if (!metadataPath || !pathCandidates.length) {
        return false;
    }

    return getFieldDefinitionCandidates(metadataPath)
        .some((candidate) => pathCandidates.includes(candidate));
};

const resolveRelatedFieldDefinition = (fieldDefinitions, path) => {
    const pathCandidates = getFieldDefinitionCandidates(path);
    if (!pathCandidates.length) {
        return null;
    }

    for (const fieldDefinition of Object.values(fieldDefinitions || {})) {
        if (!fieldDefinition || typeof fieldDefinition !== 'object') {
            continue;
        }

        const relatedFieldTypes = fieldDefinition?.relatedFieldTypes || fieldDefinition?.autoSave?.relatedFieldTypes;
        if (relatedFieldTypes && typeof relatedFieldTypes === 'object') {
            for (const [relatedPath, relatedValueType] of Object.entries(relatedFieldTypes)) {
                if (!candidateMatchesPath(relatedPath, pathCandidates)) {
                    continue;
                }

                return {
                    ...fieldDefinition,
                    field: path,
                    valueType: relatedValueType,
                };
            }
        }

        if (candidateMatchesPath(fieldDefinition?.countryCodeField, pathCandidates)) {
            return {
                ...fieldDefinition,
                field: path,
                type: 'integer',
                uiType: 'numberField',
            };
        }

        if (candidateMatchesPath(fieldDefinition?.phoneVerifiedViaOTPField, pathCandidates)) {
            return {
                ...fieldDefinition,
                field: path,
                type: 'boolean',
                uiType: 'radioBoolean',
            };
        }

        if (candidateMatchesPath(fieldDefinition?.phoneField, pathCandidates)) {
            return {
                ...fieldDefinition,
                field: path,
                type: 'string',
            };
        }
    }

    return null;
};

const resolveFieldDefinition = (fieldDefinitions, path) => {
    if (!fieldDefinitions || typeof fieldDefinitions !== 'object' || !path) {
        return null;
    }

    const diffMapStrippedPaths = [
        path.replace(/^diffMap\./, ''),
        path.replace(/\.diffMap\./g, '.'),
    ].filter((candidate) => candidate && candidate !== path);

    for (const candidate of [...getFieldDefinitionCandidates(path), ...diffMapStrippedPaths.flatMap((candidate) => getFieldDefinitionCandidates(candidate))]) {
        if (fieldDefinitions[candidate]) {
            return fieldDefinitions[candidate];
        }
    }

    return resolveRelatedFieldDefinition(fieldDefinitions, path);
};

export const createAutoSaveFieldDefinitionMap = (fields = []) => {
    if (!Array.isArray(fields)) {
        return {};
    }

    return fields.reduce((accumulator, fieldDefinition) => {
        if (!fieldDefinition || typeof fieldDefinition !== 'object') {
            return accumulator;
        }

        const keys = [
            fieldDefinition?.field,
            fieldDefinition?.fvBankProperty,
        ].filter((key) => typeof key === 'string' && key.trim());

        if (!keys.length) {
            return accumulator;
        }

        keys.forEach((key) => {
            accumulator[key] = fieldDefinition?.field
                ? fieldDefinition
                : {
                    ...fieldDefinition,
                    field: key,
                };
        });

        return accumulator;
    }, {});
};


export const resolveAutoSaveDebounceMs = (autoSaveConfig, fallbackDebounceMs = 1500) => {
    const configuredDebounceMs = Number(autoSaveConfig?.debounceMs);
    return Number.isFinite(configuredDebounceMs) && configuredDebounceMs > 0
        ? configuredDebounceMs
        : fallbackDebounceMs;
};

export const resolveAutoSaveSanitizeOptions = (autoSaveConfig) => ({
    rootOmissions: Array.isArray(autoSaveConfig?.rootOmissions) ? autoSaveConfig.rootOmissions : [],
    pathOmissions: Array.isArray(autoSaveConfig?.pathOmissions) ? autoSaveConfig.pathOmissions : [],
});

const getComparablePathCandidates = (path = '') => {
    const lodashPath = toLodashPath(path);
    const indexedPath = normalizeIndexedPath(lodashPath);

    return [...new Set([lodashPath, indexedPath].filter(Boolean))];
};

const matchesOmittedPath = (path = '', omittedPath = '') => {
    if (!path || !omittedPath) {
        return false;
    }

    const omittedCandidates = getComparablePathCandidates(omittedPath);
    const pathCandidates = getComparablePathCandidates(path);

    return omittedCandidates.some((omittedCandidate) => pathCandidates.some((pathCandidate) => (
        pathCandidate === omittedCandidate
        || pathCandidate.startsWith(`${omittedCandidate}.`)
    )));
};

export const createAutoSaveExcludePath = ({ rootOmissions = [], pathOmissions = [] } = {}) => {
    const normalizedRootOmissions = Array.isArray(rootOmissions) ? rootOmissions.filter(Boolean) : [];
    const normalizedPathOmissions = Array.isArray(pathOmissions) ? pathOmissions.filter(Boolean) : [];

    return ({ path }) => {
        if (!path) {
            return false;
        }

        if (shouldStripTransientFrontendPath(path, { includeDraftKeys: true })) {
            return true;
        }

        if (normalizedRootOmissions.some((omittedRoot) => matchesOmittedPath(path, omittedRoot))) {
            return true;
        }

        if (normalizedPathOmissions.some((omittedPath) => matchesOmittedPath(path, omittedPath))) {
            return true;
        }

        return false;
    };
};

const resolveAutoSaveStepValue = (step) => {
    if (_.isPlainObject(step)) {
        if (step?.wizard !== undefined && step?.wizard !== null) {
            return step.wizard;
        }

        if (step?.step !== undefined && step?.step !== null) {
            return step.step;
        }

        if (step?.value !== undefined && step?.value !== null) {
            return step.value;
        }
    }

    return step;
};

export const isAutoSaveEnabledForConfig = ({
    autoSaveConfig,
    step,
    context = {},
} = {}) => {
    if (!autoSaveConfig || autoSaveConfig?.enabled === false) {
        return false;
    }

    const requiredContext = Array.isArray(autoSaveConfig?.requiredContext)
        ? autoSaveConfig.requiredContext
        : [];

    if (requiredContext.some((contextKey) => !Boolean(context?.[contextKey]))) {
        return false;
    }

    const ignoreBlockedContextWhen = Array.isArray(autoSaveConfig?.ignoreBlockedContextWhen)
        ? autoSaveConfig.ignoreBlockedContextWhen
        : [];
    const shouldIgnoreBlockedContext = ignoreBlockedContextWhen.some((contextKey) => Boolean(context?.[contextKey]));

    if (!shouldIgnoreBlockedContext && _.isPlainObject(autoSaveConfig?.blockedContextValues)) {
        for (const [contextKey, blockedValues] of Object.entries(autoSaveConfig.blockedContextValues)) {
            if (!Array.isArray(blockedValues)) {
                continue;
            }

            if (blockedValues.some((blockedValue) => _.isEqual(blockedValue, context?.[contextKey]))) {
                return false;
            }
        }
    }

    const resolvedStepValue = resolveAutoSaveStepValue(step);
    const enabledStates = Array.isArray(autoSaveConfig?.enabledStates) ? autoSaveConfig.enabledStates : [];
    if (enabledStates.length) {
        if (!enabledStates.some((value) => String(value) === String(resolvedStepValue))) {
            return false;
        }
    }

    const enabledSteps = Array.isArray(autoSaveConfig?.enabledSteps) ? autoSaveConfig.enabledSteps : [];
    if (enabledSteps.length) {
        const numericStepValue = Number(resolvedStepValue);
        if (!Number.isFinite(numericStepValue) || !enabledSteps.some((value) => Number(value) === numericStepValue)) {
            return false;
        }
    }

    const enabledWizards = Array.isArray(autoSaveConfig?.enabledWizards) ? autoSaveConfig.enabledWizards : [];
    if (enabledWizards.length) {
        const wizardValue = step?.wizard ?? resolvedStepValue;
        if (!enabledWizards.some((value) => String(value) === String(wizardValue))) {
            return false;
        }
    }

    return true;
};

const inferOptionsExpectedType = (fieldDefinition) => {
    const optionValues = Array.isArray(fieldDefinition?.options)
        ? fieldDefinition.options
            .map((option) => option?.value)
            .filter((value) => value !== undefined)
        : [];

    if (!optionValues.length) {
        return null;
    }

    if (optionValues.every((value) => typeof value === 'boolean')) {
        return 'boolean';
    }

    if (optionValues.every((value) => typeof value === 'number')) {
        return 'number';
    }

    return null;
};

const inferExpectedType = (previousValue, fieldDefinition) => {
    const configuredValueType = normalizeConfiguredValueType(
        fieldDefinition?.valueType
        || fieldDefinition?.autoSave?.valueType
        || fieldDefinition?.autoSaveValueType
    );
    if (configuredValueType) {
        return configuredValueType;
    }

    const uiType = String(fieldDefinition?.uiType || '').trim().toLowerCase();
    const fieldType = String(fieldDefinition?.type || '').trim().toLowerCase();
    const optionsType = inferOptionsExpectedType(fieldDefinition);
    const checkboxActsLikeArray = fieldType === 'checkbox' && hasSelectableOptions(fieldDefinition);

    if (uiType === 'radiomultiple') {
        if (fieldType === 'array' || configuredValueType === 'array') {
            return 'array';
        }
        return optionsType || 'string';
    }

    if (NUMERIC_FIELD_TYPES.has(fieldType) || NUMERIC_UI_TYPES.has(uiType)) {
        return 'number';
    }

    if ((BOOLEAN_FIELD_TYPES.has(fieldType) || BOOLEAN_UI_TYPES.has(uiType)) && !checkboxActsLikeArray) {
        return 'boolean';
    }

    if (checkboxActsLikeArray || ARRAY_FIELD_TYPES.has(fieldType) || ARRAY_UI_TYPES.has(uiType)) {
        return 'array';
    }

    if (optionsType) {
        return optionsType;
    }

    if (STRING_FIELD_TYPES.has(fieldType)) {
        return 'string';
    }

    if (Array.isArray(previousValue)) return 'array';
    if (typeof previousValue === 'number') return 'number';
    if (typeof previousValue === 'boolean') return 'boolean';
    if (typeof previousValue === 'string') return 'string';

    return null;
};

const coerceNumberValue = (value) => {
    if (value === null || value === undefined || typeof value === 'number') {
        return value;
    }

    if (value === '') {
        return null;
    }

    if (typeof value === 'string') {
        const normalizedValue = value.trim().replace(/,/g, '').replace(/%$/, '');
        if (!normalizedValue) {
            return null;
        }

        const parsedValue = Number(normalizedValue);
        return Number.isFinite(parsedValue) ? parsedValue : undefined;
    }

    return value;
};

const coerceBooleanValue = (value) => {
    if (value === '' || value === null || value === undefined || typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();
        if (normalizedValue === 'true' || normalizedValue === 'yes') return true;
        if (normalizedValue === 'false' || normalizedValue === 'no') return false;
    }

    if (value === 1 || value === '1') return true;
    if (value === 0 || value === '0') return false;

    return value;
};

const coerceStringValue = (value) => {
    if (value === '' || value === null || value === undefined || typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    return value;
};

const coerceArrayValue = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (value === null || value === undefined || value === '') {
        return [];
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return [];
        }

        if ((trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) || (trimmedValue.startsWith('{') && trimmedValue.endsWith('}'))) {
            try {
                const parsedValue = JSON.parse(trimmedValue);
                return coerceArrayValue(parsedValue);
            } catch (error) {
                // Fall back to lightweight parsing below.
            }
        }

        return trimmedValue.includes(',')
            ? trimmedValue.split(',').map((item) => item.trim()).filter(Boolean)
            : [trimmedValue];
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return [value];
    }

    if (_.isPlainObject(value)) {
        if (value?.empty === true || value?.traversableAgain === true) {
            return [];
        }

        const numericKeys = Object.keys(value).filter((key) => /^\d+$/.test(key)).sort((left, right) => Number(left) - Number(right));
        if (numericKeys.length) {
            return numericKeys.map((key) => value[key]);
        }

        return [];
    }

    if (typeof value?.[Symbol.iterator] === 'function') {
        try {
            return Array.from(value);
        } catch (error) {
            return [];
        }
    }

    return [];
};

const coerceScalarArrayValue = (value, expectedType) => {
    if (!Array.isArray(value)) {
        return value;
    }

    const firstNonEmptyValue = value.find((item) => item !== undefined && item !== null && item !== '');
    if (firstNonEmptyValue !== undefined) {
        return firstNonEmptyValue;
    }

    if (expectedType === 'number') {
        return null;
    }

    if (expectedType === 'boolean') {
        return null;
    }

    if (expectedType === 'string') {
        return '';
    }

    return value[0];
};

const normalizeFieldValue = ({ path, value, previousValue, fieldDefinitions }) => {
    if (path && shouldStripTransientFrontendPath(path, { includeDraftKeys: true })) {
        return undefined;
    }

    const fieldDefinition = resolveFieldDefinition(fieldDefinitions, path);
    const expectedType = inferExpectedType(previousValue, fieldDefinition);

    if (expectedType === 'array') {
        const coercedArray = coerceArrayValue(value);
        const previousArray = Array.isArray(previousValue) ? previousValue : [];

        return coercedArray.reduce((accumulator, item, index) => {
            const normalizedItem = normalizeFieldValue({
                path: path ? `${path}.${index}` : String(index),
                value: item,
                previousValue: previousArray[index],
                fieldDefinitions,
            });

            if (normalizedItem !== undefined) {
                accumulator.push(normalizedItem);
            }

            return accumulator;
        }, []);
    }

    if (value === '' || value === null || value === undefined) {
        if (expectedType === 'number') {
            return value === undefined ? undefined : null;
        }
        return value;
    }

    if (Array.isArray(value) && expectedType && expectedType !== 'array') {
        return normalizeFieldValue({
            path,
            value: coerceScalarArrayValue(value, expectedType),
            previousValue,
            fieldDefinitions,
        });
    }

    if (Array.isArray(value)) {
        const previousArray = Array.isArray(previousValue) ? previousValue : [];
        return value.reduce((accumulator, item, index) => {
            const normalizedItem = normalizeFieldValue({
                path: `${path}.${index}`,
                value: item,
                previousValue: previousArray[index],
                fieldDefinitions,
            });

            if (normalizedItem !== undefined) {
                accumulator.push(normalizedItem);
            }

            return accumulator;
        }, []);
    }

    if (_.isPlainObject(value)) {
        return Object.entries(value).reduce((accumulator, [key, nestedValue]) => {
            const normalizedValue = normalizeFieldValue({
                path: path ? `${path}.${key}` : key,
                value: nestedValue,
                previousValue: _.isPlainObject(previousValue) ? previousValue?.[key] : undefined,
                fieldDefinitions,
            });
            if (normalizedValue !== undefined) {
                accumulator[key] = normalizedValue;
            }
            return accumulator;
        }, {});
    }

    if (expectedType === 'number') {
        return coerceNumberValue(value);
    }

    if (expectedType === 'boolean') {
        return coerceBooleanValue(value);
    }

    if (expectedType === 'string') {
        return coerceStringValue(value);
    }

    return value;
};

export const normalizeFieldValueMap = ({
    fieldValueMap,
    snapshotData,
    fieldDefinitions,
}) => {
    if (!fieldValueMap || typeof fieldValueMap !== 'object') {
        return {};
    }

    return Object.entries(fieldValueMap).reduce((accumulator, [path, value]) => {
        const normalizedValue = normalizeFieldValue({
            path,
            value,
            previousValue: _.get(snapshotData, toLodashPath(path)),
            fieldDefinitions,
        });
        if (normalizedValue !== undefined) {
            accumulator[path] = normalizedValue;
        }
        return accumulator;
    }, {});
};

export const normalizeRecordPayload = ({
    path = '',
    payload,
    snapshotData,
    fieldDefinitions,
}) => normalizeFieldValue({
    path,
    value: _.cloneDeep(payload),
    previousValue: snapshotData,
    fieldDefinitions,
});

export const unsetPath = (target, path) => {
    if (!target || !path) return target;
    _.unset(target, toLodashPath(path));
    return target;
};

export const unsetPaths = (target, paths = []) => {
    if (!target) return target;
    paths.forEach((path) => unsetPath(target, path));
    return target;
};

export const deleteRootKeys = (target, keys = []) => {
    if (!target) return target;
    keys.forEach((key) => {
        if (key in target) {
            delete target[key];
        }
    });
    return target;
};

export const removeHidePinCodeFlags = (target) => {
    if (!target) return target;

    ['address', 'communicationAddress', 'mailingAddress'].forEach((key) => {
        if (_.isPlainObject(target?.[key])) {
            delete target[key].hidePinCode;
        }
    });

    if (Array.isArray(target?.addressList)) {
        target.addressList = target.addressList.map((address) => {
            if (!_.isPlainObject(address)) return address;
            const nextAddress = _.cloneDeep(address);
            delete nextAddress.hidePinCode;
            return nextAddress;
        });
    }

    if (Array.isArray(target?.individuals)) {
        target.individuals = target.individuals.map((individual) => {
            if (!_.isPlainObject(individual)) return individual;
            const nextIndividual = _.cloneDeep(individual);
            ['address', 'communicationAddress', 'mailingAddress'].forEach((key) => {
                if (_.isPlainObject(nextIndividual?.[key])) {
                    delete nextIndividual[key].hidePinCode;
                }
            });
            return nextIndividual;
        });
    }

    return target;
};

export const removeTransientFrontendHelperFields = (target, path = '') => {
    if (target === null || target === undefined) {
        return target;
    }

    if (Array.isArray(target)) {
        return target.map((item, index) => removeTransientFrontendHelperFields(item, path ? `${path}.${index}` : String(index)));
    }

    if (!_.isPlainObject(target)) {
        return target;
    }

    return Object.entries(target).reduce((accumulator, [key, value]) => {
        const nextPath = path ? `${path}.${key}` : key;
        if (shouldStripTransientFrontendPath(nextPath)) {
            return accumulator;
        }

        accumulator[key] = removeTransientFrontendHelperFields(value, nextPath);
        return accumulator;
    }, {});
};

export const prepareBaseAutoSaveData = (data, { rootOmissions = [], pathOmissions = [] } = {}) => {
    if (!data || typeof data !== 'object') {
        return data;
    }

    const prepared = _.cloneDeep(data);
    removeHidePinCodeFlags(prepared);
    // This runs before diffing so unsupported helper state is suppressed for normal field
    // updates, whole-array structural saves, and create payloads in one place.
    const sanitized = removeTransientFrontendHelperFields(prepared);
    deleteRootKeys(sanitized, rootOmissions);
    unsetPaths(sanitized, pathOmissions);
    return sanitized;
};

const cloneComparableValue = (value) => {
    if (value === undefined) {
        return null;
    }
    return _.cloneDeep(value);
};

const isPlainObject = (value) => _.isPlainObject(value);

const isArrayOfPlainObjects = (value) => (
    Array.isArray(value)
    && value.every((item) => item === undefined || item === null || isPlainObject(item))
);

const shouldExcludePath = ({ path, excludePath, previousValue, nextValue }) => {
    if (!path) return false;
    if (DEFAULT_EXCLUDED_ROOT_KEYS.has(path)) return true;
    if (typeof excludePath !== 'function') return false;
    return Boolean(excludePath({ path, previousValue, nextValue }));
};

const diffAnyValue = ({ previousValue, nextValue, path, fieldValueMap, excludePath, treatAsLeafPath }) => {
    if (shouldExcludePath({ path, excludePath, previousValue, nextValue })) {
        return;
    }

    if (path && typeof treatAsLeafPath === 'function' && treatAsLeafPath(path, previousValue, nextValue)) {
        if (!_.isEqual(previousValue, nextValue)) {
            fieldValueMap[path] = cloneComparableValue(nextValue);
        }
        return;
    }

    if (Array.isArray(previousValue) || Array.isArray(nextValue)) {
        const arrayPresenceChanged = Array.isArray(previousValue) !== Array.isArray(nextValue);
        const previousArray = Array.isArray(previousValue) ? previousValue : [];
        const nextArray = Array.isArray(nextValue) ? nextValue : [];
        const shouldRecurseArrayItems = (
            !arrayPresenceChanged
            &&
            previousArray.length === nextArray.length
            && isArrayOfPlainObjects(previousArray)
            && isArrayOfPlainObjects(nextArray)
        );

        if (!shouldRecurseArrayItems) {
            // Preserve empty-array transitions like undefined -> [] so diff baselines such as
            // diffMap.entities = [] are emitted on the first add and survive reloads.
            if ((arrayPresenceChanged || !_.isEqual(previousArray, nextArray)) && path) {
                fieldValueMap[path] = cloneComparableValue(nextValue);
            }
            return;
        }

        for (let index = 0; index < nextArray.length; index += 1) {
            const childPath = path ? `${path}.${index}` : String(index);
            diffAnyValue({
                previousValue: previousArray[index],
                nextValue: nextArray[index],
                path: childPath,
                fieldValueMap,
                excludePath,
                treatAsLeafPath,
            });
        }
        return;
    }

    if (isPlainObject(previousValue) || isPlainObject(nextValue)) {
        const previousObject = isPlainObject(previousValue) ? previousValue : {};
        const nextObject = isPlainObject(nextValue) ? nextValue : {};
        const keys = new Set([
            ...Object.keys(previousObject || {}),
            ...Object.keys(nextObject || {}),
        ]);

        keys.forEach((key) => {
            const childPath = path ? `${path}.${key}` : key;
            diffAnyValue({
                previousValue: previousObject?.[key],
                nextValue: nextObject?.[key],
                path: childPath,
                fieldValueMap,
                excludePath,
                treatAsLeafPath,
            });
        });
        return;
    }

    if (!_.isEqual(previousValue, nextValue) && path) {
        fieldValueMap[path] = cloneComparableValue(nextValue);
    }
};

export const buildFieldValueMap = ({
    snapshot,
    current,
    excludePath,
    treatAsLeafPath,
}) => {
    const fieldValueMap = {};
    diffAnyValue({
        previousValue: snapshot || {},
        nextValue: current || {},
        path: '',
        fieldValueMap,
        excludePath,
        treatAsLeafPath,
    });
    return fieldValueMap;
};

export const countChangedPaths = (options) => Object.keys(buildFieldValueMap(options)).length;

export const createFieldUpdateOperation = ({ endpoint, preparedData, snapshotData, fieldDefinitions, fieldValueMap, label, recordKey, meta = {} }) => {
    const normalizedFieldValueMap = normalizeFieldValueMap({
        fieldValueMap,
        snapshotData,
        fieldDefinitions,
    });
    const clearedFieldPaths = Object.entries(normalizedFieldValueMap || {}).reduce((accumulator, [path, value]) => {
        if (value === null && _.get(snapshotData, toLodashPath(path)) !== undefined) {
            accumulator.push(path);
        }
        return accumulator;
    }, []);

    if (!endpoint || !preparedData?._id || !normalizedFieldValueMap || !Object.keys(normalizedFieldValueMap).length) {
        return null;
    }

    return {
        type: 'field',
        endpoint,
        label,
        recordKey,
        priority: 50,
        meta: {
            ...meta,
            clearedFieldPaths,
        },
        changeCount: Object.keys(normalizedFieldValueMap).length,
        payload: {
            _id: preparedData._id,
            version: Number.isFinite(Number(preparedData?.version)) ? Number(preparedData.version) : 0,
            fieldValueMap: normalizedFieldValueMap,
        },
    };
};

export const createFullUpdateOperation = ({ endpoint, preparedData, changeCount, label, recordKey, meta = {} }) => {
    if (!endpoint || !preparedData?._id || !changeCount) {
        return null;
    }

    return {
        type: 'full',
        endpoint,
        label,
        recordKey,
        priority: 50,
        meta,
        changeCount,
        payload: _.cloneDeep(preparedData),
    };
};

export const createPostOperation = ({ endpoint, payload, changeCount = 1, label, recordKey, priority = 10, meta = {} }) => {
    if (!endpoint || !payload || typeof payload !== 'object') {
        return null;
    }

    return {
        type: 'create',
        endpoint,
        label,
        recordKey,
        priority,
        changeCount,
        meta,
        payload: _.cloneDeep(payload),
    };
};

export const createDeleteOperation = ({ endpoint, payload = null, changeCount = 1, label, recordKey, priority = 90, meta = {} }) => {
    if (!endpoint) {
        return null;
    }

    return {
        type: 'delete',
        endpoint,
        label,
        recordKey,
        priority,
        changeCount,
        meta,
        payload: payload ? _.cloneDeep(payload) : null,
    };
};

export const createApplicationFormUpdateOperation = ({ applicationForm, cyclosUserName, changeCount = 1, label = 'application-form', recordKey = 'applicationForm' }) => {
    if (!applicationForm?._id || !Number.isFinite(changeCount) || changeCount <= 0) {
        return null;
    }

    return createFieldUpdateOperation({
        endpoint: '/api/application-form/update-field',
        preparedData: {
            ..._.cloneDeep(applicationForm),
            version: applicationForm?.version,
        },
        snapshotData: _.cloneDeep(applicationForm),
        fieldValueMap: {
            cyclosUserName,
        },
        label,
        recordKey,
    });
};

export const createPlan = ({ operations = [], changeCount }) => {
    const compactOperations = operations
        .filter(Boolean)
        .sort((left, right) => {
            const leftPriority = Number.isFinite(Number(left?.priority)) ? Number(left.priority) : 50;
            const rightPriority = Number.isFinite(Number(right?.priority)) ? Number(right.priority) : 50;
            return leftPriority - rightPriority;
        });
    const resolvedChangeCount = Number.isFinite(changeCount)
        ? changeCount
        : compactOperations.reduce((sum, operation) => sum + (operation?.changeCount || 0), 0);

    return {
        operations: compactOperations,
        changeCount: resolvedChangeCount,
    };
};
